import { Feather, MaterialIcons } from "@expo/vector-icons";
import Slider from "@react-native-community/slider";
import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Dimensions,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  ActivityIndicator,
  Animated,
} from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Centered,
  DateTimeSelect,
  JobTypeFilterTabs,
  Row,
  Input,
  BackButton,
  CountrySelect,
} from "../../components/all";
import DatePostedSelect from "../../components/DatePostedSelect";
import Collapsible from "react-native-collapsible";
import {
  resetSearchFilter,
  setSearchFilter,
} from "../../reducers/search/searchFilter";
import { getProfile } from "../../services/profile";
import { useQuery } from "@tanstack/react-query";
import { getProfessionTypes, getProfessions } from "../../services/shared";
import { normalize } from "../../utils/normalize";
import { getCurrencySymbol, getPriceMaxLength } from "../../utils/currency";
import { JOB_TYPES } from "../../constants/jobTypes";
import {
  getLocalizedProfessionTypeName,
  getLocalizedProfessionName,
  filterProfessions,
  extractProfessionTitleAndSubtitle,
  sortProfessionsGeneralFirst
} from "../../utils/professionUtils";
import colors from "../../constants/colors";

export default function SearchFilter(props) {
  const safeInsets = useSafeAreaInsets();
  const dispatch = useDispatch();
  const { filters, isClear } = useSelector((state) => state.searchFilter);
  const { isDoctor, isDev, country, currencyPreset } = useSelector((state) => state.user);
  const [selectedProfessions, setSelectedProfessions] = useState(
    filters.profession_ids || []
  );
  const [selectedJobType, setSelectedJobType] = useState(filters.jobType || 0);
  const [internationalFilter, setInternationalFilter] = useState(
    filters.internationalFilter || { type: 'all', countries: [] }
  );
  const [selectedInternationalCountry, setSelectedInternationalCountry] = useState("");
  const [expandedTypes, setExpandedTypes] = useState([]);
  const [animatedValues, setAnimatedValues] = useState({});
  const [arrowAnimations, setArrowAnimations] = useState({});

  // Icon mapping for profession types
  const professionIcons = {
    'a': { icon: 'medical-services', color: '#4B9BFF', bgColor: '#E3F2FF' },    // Doctor
    'b': { icon: 'local-hospital', color: '#10B981', bgColor: '#D1FAE5' },      // Nurse
    'c': { icon: 'medication', color: '#F59E0B', bgColor: '#FEF3C7' },          // Dentist
    'd': { icon: 'healing', color: '#8B5CF6', bgColor: '#EDE9FE' },             // Dental Surgery Assistant
    'e-x': { icon: 'medical-services', color: '#EF4444', bgColor: '#FEE2E2' }, // Medical Assistant
    'f-x': { icon: 'assignment', color: '#EC4899', bgColor: '#FCE7F3' },       // Clinic Assistant
    'g-x': { icon: 'biotech', color: '#6366F1', bgColor: '#EEF2FF' },          // Research & Education
    'h-x': { icon: 'home-health', color: '#14B8A6', bgColor: '#CCFBF1' },      // Caregiver
    'i-x': { icon: 'camera-alt', color: '#F97316', bgColor: '#FFEDD5' },       // Radiographer
    'j-x': { icon: 'graphic-eq', color: '#8B5CF6', bgColor: '#EDE9FE' },       // Sonographer
    'k-x': { icon: 'medication', color: '#22C55E', bgColor: '#DCFCE7' },       // Pharmacist
    'l-x': { icon: 'science', color: '#3B82F6', bgColor: '#DBEAFE' },          // Laboratory
    'm-x': { icon: 'build', color: '#64748B', bgColor: '#F1F5F9' },            // Technician
    'n-x': { icon: 'settings', color: '#71717A', bgColor: '#F4F4F5' },         // Technologist
    'o-x': { icon: 'directions-run', color: '#0EA5E9', bgColor: '#E0F2FE' },   // Physiotherapist
    'p-x': { icon: 'psychology', color: '#D946EF', bgColor: '#FAE8FF' },       // Therapist
    'q-x': { icon: 'health-and-safety', color: '#059669', bgColor: '#D1FAE5' },// Allied Health
    'r-x': { icon: 'support-agent', color: '#78716C', bgColor: '#F5F5F4' },    // Healthcare Support
    's-x': { icon: 'business-center', color: '#0891B2', bgColor: '#CFFAFE' },  // Healthcare Management
    't-x': { icon: 'account-balance', color: '#7C3AED', bgColor: '#EDE9FE' },  // Non-Healthcare Management
    'u-x': { icon: 'miscellaneous-services', color: '#CA8A04', bgColor: '#FEF9C3' }, // Non-Healthcare Support
    'default': { icon: 'work-outline', color: '#4B9BFF', bgColor: '#E3F2FF' }
  };

  // Fetch profession types
  const {
    data: professionTypesData,
    isLoading: typesLoading,
    error: typesError,
    refetch: refetchTypes,
  } = useQuery(['professionTypes'], () => getProfessionTypes(), {
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Fetch professions
  const {
    data: professionsData,
    isLoading: professionsLoading,
    error: professionsError,
    refetch: refetchProfessions,
  } = useQuery(['professions', country], () => getProfessions(country || "MY"), {
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const {
    data: profileQuery,
    refetch: profileRefetch,
    isLoading: profileLoading,
  } = useQuery("profile", getProfile, {});

  const [filterText, setFilterText] = useState("");

  const professionTypes = professionTypesData?.data?.data || [];
  const professions = (professionsData?.data?.data || []).sort((a, b) => {
    const nameA = getLocalizedProfessionName(a, country || 'MY');
    const nameB = getLocalizedProfessionName(b, country || 'MY');
    return nameA.localeCompare(nameB);
  });

  // Get doctor's profession type from profile
  const doctorProfessionTypeCode = profileQuery?.data?.data?.profession?.profession_type_code;
  const doctorProfessionType = profileQuery?.data?.data?.profession?.profession_type;

  // Preselect user's professions from profile if no filters are set
  useEffect(() => {
    // Only preselect if:
    // 1. Profile data is loaded
    // 2. User has professions in their profile
    // 3. No profession filters are already set (to preserve user's manual selections)
    const profileProfessions = profileQuery?.data?.data?.professions;

    if (profileProfessions &&
      Array.isArray(profileProfessions) &&
      profileProfessions.length > 0 &&
      (!filters.profession_ids || filters.profession_ids.length === 0)) {

      // Extract profession IDs from the profile's professions array
      const professionIds = profileProfessions.map(prof => prof.id);
      setSelectedProfessions(professionIds);
    }
  }, [profileQuery]);

  // Helper to get the parent profession type code
  // - 'a-a-1' -> 'a' (Doctor specialties)
  // - 'e-x-1' -> 'e-x' (Medical Assistant, etc.)
  const getParentTypeCode = (code) => {
    if (!code) return null;
    const parts = code.split('-');
    if (parts.length >= 2 && parts[1] === 'x') {
      return `${parts[0]}-x`; // e-x, f-x, etc.
    }
    return parts[0]; // a, b, c, d
  };

  // Organize professions by type - but only for doctor's profession type if available
  const organizedProfessions = useMemo(() => {
    const hierarchy = {};

    // If doctor has a profession type, only show that type
    if (isDoctor && doctorProfessionTypeCode) {
      const parentCode = getParentTypeCode(doctorProfessionTypeCode);

      // Find the parent type from profession types
      const parentType = professionTypes.find(type => type.code === parentCode);
      if (parentType) {
        hierarchy[parentCode] = {
          ...parentType,
          professions: []
        };
      }

      // Add only professions matching doctor's profession type
      professions.forEach(profession => {
        const profParentCode = getParentTypeCode(profession.profession_type_code);
        if (profParentCode === parentCode && hierarchy[parentCode]) {
          hierarchy[parentCode].professions.push(profession);
        }
      });
    } else {
      // For non-doctors or when no profession type is set, show all types
      // Include top-level types: single letters (a, b, c, d) and letter-x format (e-x, f-x, etc.)
      professionTypes.forEach(type => {
        const code = type.code;
        const isTopLevel = !code.includes('-') || /^[a-z]-x$/i.test(code);

        if (isTopLevel) {
          if (!hierarchy[code]) {
            hierarchy[code] = {
              ...type,
              professions: []
            };
          }
        }
      });

      // Add all professions to their types
      professions.forEach(profession => {
        const typeCode = profession.profession_type_code;
        if (typeCode) {
          const parentCode = getParentTypeCode(typeCode);
          if (hierarchy[parentCode]) {
            hierarchy[parentCode].professions.push(profession);
          }
        }
      });
    }

    // Pin each list's "general" entry (e.g. General Practitioner) to the top.
    // Lists here are flattened up to the parent type, so the helper reads each
    // profession's own type code and leaves specialist entries in place.
    Object.values(hierarchy).forEach(type => {
      type.professions = sortProfessionsGeneralFirst(
        type.professions,
        undefined,
        country || 'MY'
      );
    });

    return hierarchy;
  }, [professionTypes, professions, isDoctor, doctorProfessionTypeCode, country]);

  // Filter professions based on search
  const filteredProfessions = useMemo(() => {
    if (!filterText) return organizedProfessions;

    const filtered = {};
    const lowerSearch = filterText.toLowerCase();

    Object.entries(organizedProfessions).forEach(([code, type]) => {
      // Check if parent type matches
      const parentName = getLocalizedProfessionTypeName(type, country);
      const parentMatches = parentName.toLowerCase().includes(lowerSearch);

      // Check if any profession matches
      const matchingProfessions = type.professions.filter(profession => {
        const professionName = getLocalizedProfessionName(profession, country);
        return professionName.toLowerCase().includes(lowerSearch);
      });

      // Include if parent matches or has matching professions
      if (parentMatches || matchingProfessions.length > 0) {
        filtered[code] = {
          ...type,
          professions: parentMatches ? type.professions : matchingProfessions
        };
      }
    });

    return filtered;
  }, [organizedProfessions, filterText, country]);
  const [minPrice, setMinPrice] = useState(filters.minPrice);
  const [maxPrice, setMaxPrice] = useState(filters.maxPrice);
  const [allDateTime, setAllDateTime] = useState({});
  const [datePosted, setDatePosted] = useState(filters.datePosted || { created_min: null, created_max: null });

  const [mapDistance, setMapDistance] = useState(filters.mapDistance || 40);
  const [mapDistanceText, setMapDistanceText] = useState(
    filters.mapDistance || 50
  );

  const isFullTime = selectedJobType > 0;
  const isInternational = selectedJobType === 2;

  // Initialize animations for each profession type
  useEffect(() => {
    const newAnimatedValues = {};
    const newArrowAnimations = {};

    Object.keys(filteredProfessions).forEach((code, index) => {
      if (!animatedValues[code]) {
        newAnimatedValues[code] = new Animated.Value(0);
        newArrowAnimations[code] = new Animated.Value(0);
      }
    });

    if (Object.keys(newAnimatedValues).length > 0) {
      setAnimatedValues(prev => ({ ...prev, ...newAnimatedValues }));
      setArrowAnimations(prev => ({ ...prev, ...newArrowAnimations }));
    }
  }, [filteredProfessions]);

  // Animate entrance when data loads
  useEffect(() => {
    const isLoading = typesLoading || professionsLoading;
    if (!isLoading && Object.keys(animatedValues).length > 0) {
      const animations = Object.entries(animatedValues).map(([code, animValue], index) => {
        return Animated.timing(animValue, {
          toValue: 1,
          duration: 400,
          delay: index * 100,
          useNativeDriver: true,
        });
      });

      Animated.parallel(animations).start();
    }
  }, [typesLoading, professionsLoading, animatedValues]);

  const toggleExpanded = (typeCode) => {
    setExpandedTypes(prev => {
      const isExpanding = !prev.includes(typeCode);

      // Animate arrow rotation
      if (arrowAnimations[typeCode]) {
        Animated.spring(arrowAnimations[typeCode], {
          toValue: isExpanding ? 1 : 0,
          useNativeDriver: true,
          tension: 50,
          friction: 7,
        }).start();
      }

      if (prev.includes(typeCode)) {
        return prev.filter(code => code !== typeCode);
      }
      return [...prev, typeCode];
    });
  };

  const toggleProfessionSelection = (professionId) => {
    setSelectedProfessions(prev => {
      if (prev.includes(professionId)) {
        return prev.filter(id => id !== professionId);
      }
      return [...prev, professionId];
    });
  };

  useEffect(() => {
    if (filters) {
      setAllDateTime({
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        startTime: filters?.startTime,
        endTime: filters?.endTime,
      });
    }
  }, [filters]);

  let convertSearchFilters = (data) => {
    let params = {};
    data.location && (params["location"] = data.location);
    data.maxPrice && (params["maxPrice"] = data.maxPrice);
    data.minPrice && (params["minPrice"] = data.minPrice);
    data.mapDistance > 5 && (params["mapDistance"] = data.mapDistance);
    data.internationalFilter && (params["internationalFilter"] = data.internationalFilter);
    data.profession_ids?.length &&
      (params["profession_ids"] = data.profession_ids);
    data.startDate && (params["startDate"] = data.startDate);
    data.endDate && (params["endDate"] = data.endDate);
    data.startTime && (params["startTime"] = data.startTime);
    data.endTime && (params["endTime"] = data.endTime);
    // Store jobType to indicate selected job type tab
    params["jobType"] = data.jobType;
    data.states && (params["states"] = data.states);
    if (data.datePosted) {
      data.datePosted.created_min && (params["created_min"] = data.datePosted.created_min);
      data.datePosted.created_max && (params["created_max"] = data.datePosted.created_max);
    }
    return params;
  };

  return (
    <>
      <BackButton />

      <View style={{ backgroundColor: "white", padding: 24, paddingBottom: 4, paddingTop: 0 }}>
        <Row centered style={{ marginBottom: 0 }}>
          <Feather name={"filter"} size={20} color="black" />
          <Text style={styles.jobsAvailableTitle}>{`Search Filters`}</Text>
        </Row>
      </View>
      {isDev ? (
        <JobTypeFilterTabs
          isDev={isDev}
          selectedJobType={selectedJobType}
          variant="pills"
          onJobTypeChange={(index) => {
            setSelectedJobType(index);
            // Set international filter when switching to International
            if (index === 4) {
              setInternationalFilter({ type: 'international', countries: [] });
            } else {
              setInternationalFilter({ type: 'all', countries: [] });
            }
          }}
          showInternational={false}
          onInternationalChange={(value) => {
            if (value) {
              setInternationalFilter({ type: 'international', countries: [] });
            } else {
              setInternationalFilter({ type: 'all', countries: [] });
            }
          }}
          internationalStyle={{
            marginLeft: 12
          }}
          internationalFilter={internationalFilter}
          style={{ marginBottom: 0 }}
        />
      ) : null}

      <KeyboardAwareScrollView extraHeight={200}>
        <ScrollView
          style={{
            padding: 24,
            paddingTop: 24,
            backgroundColor: "white",
          }}
        >
          {isFullTime || isInternational ? (
            <>
              <Text style={{ fontSize: normalize(10), fontWeight: "bold" }}>
                DATE POSTED
              </Text>
              <DatePostedSelect
                value={datePosted}
                onChange={(dateRange) => {
                  setDatePosted(dateRange);
                }}
                placeholder="Select Date Posted"
                style={{ marginVertical: 12 }}
              />
            </>
          ) : (
            <>
              <Text style={{ fontSize: normalize(10), fontWeight: "bold" }}>
                DATE & TIME RANGE
              </Text>
              <DateTimeSelect
                initialData={allDateTime}
                onChange={(dates) => {
                  setAllDateTime(dates);
                }}
              />
            </>
          )}
          <View style={styles.divider} />

          <>
            <Row
              style={{ justifyContent: "space-between", alignItems: "center", marginBottom: -8 }}
            >
              <Text style={styles.sectionTitle}>{isFullTime || isInternational ? "LOCATION" : "MAP DISTANCE (RADIUS)"}</Text>
              <TouchableOpacity
                onPress={() => {
                  props.navigation.navigate("SearchLocationScreen", {
                    focusLocation: true,
                  });
                }}
              >
                <Row
                  style={{
                    alignSelf: "flex-start",
                    borderWidth: 1,
                    borderColor: "rgba(0,0,0,0.1)",
                    padding: 4,
                    paddingHorizontal: 16,
                    marginTop: 10,
                    borderRadius: 24,
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <Text style={{ fontSize: normalize(13), marginRight: 8 }}>
                    {`Change Location`}
                  </Text>
                  <Feather name="map-pin" size={13} color="black" />
                </Row>
              </TouchableOpacity>
            </Row>
            <View>
              {filters.states?.length > 0 ? (
                <Row style={{ flexWrap: "wrap" }}>
                  {filters.states?.map((state, index) => {
                    return (
                      <>
                        <TouchableOpacity
                          onPress={() => {
                            props.navigation.navigate("SearchLocationScreen", {
                              focusLocation: true,
                            });
                          }}
                        >
                          <Row
                            style={{
                              paddingHorizontal: 16,
                              paddingVertical: 8,
                              backgroundColor: '#F5F5F5',
                              borderRadius: 20,
                              marginRight: 8,
                              marginBottom: 8,
                              borderWidth: 1,
                              borderColor: '#E0E0E0',
                            }}
                          >
                            <Text style={{ fontSize: normalize(13) }}>
                              {state}
                            </Text>
                          </Row>
                        </TouchableOpacity>
                      </>
                    )
                  })}
                </Row>
              ) : null}
            </View>
          </>
          {isInternational ? (
            <View >
              <Text style={{
                fontSize: normalize(13),
                fontWeight: "600",
                color: "#333",
                marginBottom: 12,
                marginTop: 24,
              }}>
                Select countries to see international jobs from:
              </Text>

              {/* Country Select Dropdown */}
              <View style={{ marginBottom: 16 }}>
                <CountrySelect
                  value={selectedInternationalCountry}
                  onChange={(countryCode) => {
                    setSelectedInternationalCountry(countryCode);
                    if (countryCode && !internationalFilter.countries.includes(countryCode)) {
                      setInternationalFilter({
                        ...internationalFilter,
                        countries: [...internationalFilter.countries, countryCode]
                      });
                    }
                  }}
                  placeholder="Add a country"
                />
              </View>

              {/* Selected Countries List */}
              {internationalFilter.countries.length > 0 && (
                <View>
                  <Text style={{
                    fontSize: normalize(12),
                    color: "#666",
                    marginBottom: 8,
                  }}>
                    Selected countries:
                  </Text>
                  {internationalFilter.countries.map((countryCode) => (
                    <TouchableOpacity
                      key={countryCode}
                      onPress={() => {
                        const countries = internationalFilter.countries.filter(c => c !== countryCode);
                        setInternationalFilter({ ...internationalFilter, countries });
                        if (selectedInternationalCountry === countryCode) {
                          setSelectedInternationalCountry("");
                        }
                      }}
                      style={{ marginBottom: 8 }}
                    >
                      <Row centered>
                        <View style={{
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          borderWidth: 2,
                          borderColor: '#4B9BFF',
                          marginRight: 12,
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          <Feather name="check" size={14} color="#4B9BFF" />
                        </View>
                        <Text style={{ fontSize: normalize(14), color: '#333' }}>
                          {countryCode === 'SG' && '🇸🇬 Singapore'}
                          {countryCode === 'PH' && '🇵🇭 Philippines'}
                          {countryCode === 'MY' && '🇲🇾 Malaysia'}
                          {!['SG', 'PH', 'MY'].includes(countryCode) && countryCode}
                        </Text>
                        <Text style={{
                          fontSize: normalize(12),
                          color: '#999',
                          marginLeft: 8
                        }}>
                          (tap to remove)
                        </Text>
                      </Row>
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              {/* Quick Select Options */}
              <View style={{ marginTop: 16 }}>
                <Text style={{
                  fontSize: normalize(12),
                  color: "#666",
                  marginBottom: 8,
                }}>
                  Quick select:
                </Text>
                <Row style={{ flexWrap: 'wrap' }}>
                  <TouchableOpacity
                    onPress={() => {
                      const countries = internationalFilter.countries.includes('SG')
                        ? internationalFilter.countries.filter(c => c !== 'SG')
                        : [...internationalFilter.countries, 'SG'];
                      setInternationalFilter({ ...internationalFilter, countries });
                    }}
                    style={{
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      backgroundColor: internationalFilter.countries.includes('SG') ? '#E3F2FF' : '#F5F5F5',
                      borderRadius: 20,
                      marginRight: 8,
                      marginBottom: 8,
                      borderWidth: 1,
                      borderColor: internationalFilter.countries.includes('SG') ? '#4B9BFF' : '#E0E0E0',
                    }}
                  >
                    <Text style={{
                      fontSize: normalize(13),
                      color: internationalFilter.countries.includes('SG') ? '#4B9BFF' : '#666'
                    }}>
                      🇸🇬 Singapore
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => {
                      const countries = internationalFilter.countries.includes('PH')
                        ? internationalFilter.countries.filter(c => c !== 'PH')
                        : [...internationalFilter.countries, 'PH'];
                      setInternationalFilter({ ...internationalFilter, countries });
                    }}
                    style={{
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      backgroundColor: internationalFilter.countries.includes('PH') ? '#E3F2FF' : '#F5F5F5',
                      borderRadius: 20,
                      marginRight: 8,
                      marginBottom: 8,
                      borderWidth: 1,
                      borderColor: internationalFilter.countries.includes('PH') ? '#4B9BFF' : '#E0E0E0',
                    }}
                  >
                    <Text style={{
                      fontSize: normalize(13),
                      color: internationalFilter.countries.includes('PH') ? '#4B9BFF' : '#666'
                    }}>
                      🇵🇭 Philippines
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => {
                      const countries = internationalFilter.countries.includes('MY')
                        ? internationalFilter.countries.filter(c => c !== 'MY')
                        : [...internationalFilter.countries, 'MY'];
                      setInternationalFilter({ ...internationalFilter, countries });
                    }}
                    style={{
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      backgroundColor: internationalFilter.countries.includes('MY') ? '#E3F2FF' : '#F5F5F5',
                      borderRadius: 20,
                      marginBottom: 8,
                      borderWidth: 1,
                      borderColor: internationalFilter.countries.includes('MY') ? '#4B9BFF' : '#E0E0E0',
                    }}
                  >
                    <Text style={{
                      fontSize: normalize(13),
                      color: internationalFilter.countries.includes('MY') ? '#4B9BFF' : '#666'
                    }}>
                      🇲🇾 Malaysia
                    </Text>
                  </TouchableOpacity>
                </Row>
              </View>
            </View>
          ) :
            null
          }



          {isFullTime || isInternational ? null :
            <Row centered style={{ justifyContent: "space-around" }}>
              <Slider
                style={{
                  width: Dimensions.get("window").width - 180,
                  height: 40,
                }}
                value={mapDistance}
                minimumValue={5}
                maximumValue={50}
                minimumTrackTintColor="rgba(0,0,0,0.2)"
                maximumTrackTintColor="rgba(0,0,0,0.2)"
                step={1}
                onValueChange={(val) => setMapDistanceText(val)}
                onSlidingComplete={(val) => setMapDistance(val)}
              />
              <Text style={styles.distanceInput}>
                {`${mapDistanceText}`}
                <Text style={{ fontSize: normalize(12) }}> KM</Text>
              </Text>
            </Row>
          }

          <View style={styles.divider} />

          <Text style={styles.sectionTitle}>
            SALARY RANGE {`${isFullTime || isInternational ? "" : "(HOURLY RATE)"}`}
          </Text>
          <Row
            style={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <Input
              placeholder="Min Salary"
              bordered={true}
              icon={<Text style={styles.priceIcon}>{getCurrencySymbol(currencyPreset || country || 'MY')}</Text>}
              containerStyle={{ width: "42%" }}
              inputStyle={{ width: "42%" }}
              keyboardType="numeric"
              maxLength={getPriceMaxLength(currencyPreset || country || 'MY')}
              onChangeText={(val) => {
                const cleanNumber = val.replace(/[^0-9]/g, "");
                setMinPrice(cleanNumber);
              }}
              value={minPrice}
            />
            <Text
              style={{
                fontWeight: "bold",
                fontSize: normalize(28),
                color: "grey",
              }}
            >
              ~
            </Text>
            <Input
              placeholder="Max Salary"
              icon={<Text style={styles.priceIcon}>{getCurrencySymbol(currencyPreset || country || 'MY')}</Text>}
              bordered={true}
              containerStyle={{ width: "42%" }}
              inputStyle={{ width: "42%" }}
              value={maxPrice}
              keyboardType="numeric"
              maxLength={getPriceMaxLength(currencyPreset || country || 'MY')}
              onChangeText={(val) => {
                const cleanNumber = val.replace(/[^0-9]/g, "");
                setMaxPrice(cleanNumber);
              }}
            />
          </Row>
          <View style={styles.divider} />


          <Text style={styles.sectionTitle}>
            {isDoctor && doctorProfessionTypeCode ? 'CLINICAL EXPERTISE' : 'CLINICAL EXPERTISE'}
          </Text>



          <Input
            icon={<Feather name={"search"} />}
            placeholder={isDoctor && doctorProfessionTypeCode ? "Search Clinical Expertise" : "Search Clinical Expertise"}
            onChangeText={(text) => {
              setFilterText(text);
              // Auto-expand when searching
              if (text.length > 0) {
                setExpandedTypes(Object.keys(filteredProfessions));
              } else {
                setExpandedTypes([]);
              }
            }}
            value={filterText}
            bordered={true}
          />

          {/* Show loading state */}
          {(typesLoading || professionsLoading) ? (
            <View style={{ marginTop: 32, alignItems: 'center' }}>
              <ActivityIndicator size="large" color="#4B9BFF" />
              <Text style={{ marginTop: 16, fontSize: normalize(14), color: '#666' }}>
                Loading professions...
              </Text>
            </View>
          ) : (typesError || professionsError) ? (
            <View style={{ marginTop: 32, alignItems: 'center', paddingHorizontal: 16 }}>
              <Text style={{ fontSize: normalize(14), textAlign: 'center', marginBottom: 16, color: '#666' }}>
                Unable to load professions. Please check your internet connection and try again.
              </Text>
              <Button
                onPress={() => {
                  refetchTypes();
                  refetchProfessions();
                }}
                label="Retry"
                type="primary"
                style={{ paddingHorizontal: 32 }}
              />
            </View>
          ) : (
            <View style={{ marginTop: 16 }}>
              {Object.entries(filteredProfessions).map(([parentCode, parentType], index) => {
                // For doctors with single profession type, always expand it
                const isSingleType = isDoctor && doctorProfessionTypeCode && Object.keys(filteredProfessions).length === 1;
                const isExpanded = isSingleType || expandedTypes.includes(parentCode) || filterText.length > 0;
                const iconConfig = professionIcons[parentCode] || professionIcons.default;
                const animValue = animatedValues[parentCode] || new Animated.Value(1);
                const arrowAnim = arrowAnimations[parentCode] || new Animated.Value(0);

                return (
                  <Animated.View
                    key={parentCode}
                    style={{
                      marginBottom: 16,
                      opacity: animValue,
                      transform: [
                        {
                          translateY: animValue.interpolate({
                            inputRange: [0, 1],
                            outputRange: [20, 0],
                          }),
                        },
                      ],
                    }}
                  >
                    {/* Parent Type (Doctor, Nurse, etc.) - Hide for single type */}
                    {!isSingleType && (
                      <TouchableOpacity
                        onPress={() => toggleExpanded(parentCode)}
                        activeOpacity={0.7}
                        style={{
                          backgroundColor: isExpanded ? iconConfig.bgColor : "white",
                          paddingHorizontal: 20,
                          paddingVertical: 18,
                          borderRadius: 20,
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderWidth: 2,
                          borderColor: isExpanded ? iconConfig.color : "#F3F4F6",
                          shadowColor: "#000",
                          shadowOffset: {
                            width: 0,
                            height: 4,
                          },
                          shadowOpacity: isExpanded ? 0.15 : 0.08,
                          shadowRadius: 12,
                          elevation: 3,
                        }}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                          <View
                            style={{
                              width: 48,
                              height: 48,
                              backgroundColor: isExpanded ? iconConfig.color : iconConfig.bgColor,
                              borderRadius: 24,
                              marginRight: 16,
                              justifyContent: "center",
                              alignItems: "center"
                            }}
                          >
                            <MaterialIcons
                              name={iconConfig.icon}
                              size={24}
                              color={isExpanded ? "white" : iconConfig.color}
                            />
                          </View>
                          <View style={{ flex: 1 }}>
                            <Text style={{
                              fontSize: 18,
                              fontWeight: "700",
                              color: isExpanded ? iconConfig.color : "#1F2937",
                              letterSpacing: -0.5,
                            }}>
                              {getLocalizedProfessionTypeName(parentType, country)}
                            </Text>
                            {parentType.professions.length > 0 && (
                              <Text style={{
                                fontSize: 13,
                                color: isExpanded ? "#4B5563" : "#6B7280",
                                marginTop: 4,
                                letterSpacing: -0.1,
                              }}>
                                {parentType.professions.length} specialization{parentType.professions.length !== 1 ? 's' : ''}
                              </Text>
                            )}
                          </View>
                        </View>
                        <Animated.View
                          style={{
                            transform: [
                              {
                                rotate: arrowAnim.interpolate({
                                  inputRange: [0, 1],
                                  outputRange: ['0deg', '90deg'],
                                }),
                              },
                            ],
                          }}
                        >
                          <MaterialIcons
                            name="chevron-right"
                            size={24}
                            color={isExpanded ? iconConfig.color : "#9CA3AF"}
                          />
                        </Animated.View>
                      </TouchableOpacity>
                    )}

                    {/* Professions */}
                    <Collapsible collapsed={!isExpanded}>
                      <View style={{ marginTop: isSingleType ? 0 : 12, marginLeft: 0 }}>
                        {parentType.professions.map((profession, professionIndex) => {
                          const isSelected = selectedProfessions.includes(profession.id);
                          const professionName = getLocalizedProfessionName(profession, country);
                          const { title, subtitle } = extractProfessionTitleAndSubtitle(professionName);

                          return (
                            <Animated.View
                              key={profession.id}
                              style={{
                                opacity: isExpanded ? 1 : 0,
                                transform: [
                                  {
                                    translateX: isExpanded ? 0 : -20,
                                  },
                                  {
                                    scale: isSelected ? 0.98 : 1,
                                  },
                                ],
                              }}
                            >
                              <TouchableOpacity
                                onPress={() => toggleProfessionSelection(profession.id)}
                                activeOpacity={0.7}
                                style={{
                                  backgroundColor: "white",
                                  paddingHorizontal: 20,
                                  paddingVertical: 16,
                                  marginBottom: 8,
                                  borderRadius: 16,
                                  borderWidth: 2,
                                  borderColor: isSelected
                                    ? colors.primary
                                    : "#E5E7EB",
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  shadowColor: "#000",
                                  shadowOffset: {
                                    width: 0,
                                    height: 2,
                                  },
                                  shadowOpacity: isSelected ? 0.1 : 0.05,
                                  shadowRadius: 6,
                                  elevation: isSelected ? 2 : 1,
                                }}
                              >
                                <View
                                  style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: 6,
                                    borderWidth: 2,
                                    borderColor: isSelected ? colors.primary : "#D1D5DB",
                                    backgroundColor: isSelected ? colors.primary : "white",
                                    marginRight: 12,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  {isSelected && (
                                    <MaterialIcons
                                      name="check"
                                      size={12}
                                      color="white"
                                    />
                                  )}
                                </View>
                                <View style={{ flex: 1 }}>
                                  <Text style={{
                                    fontSize: 12,
                                    color: isSelected
                                      ? colors.primary
                                      : "#374151",
                                    fontWeight: isSelected
                                      ? "600"
                                      : "400",
                                    letterSpacing: -0.2,
                                  }}>
                                    {title}
                                  </Text>
                                  {subtitle && (
                                    <Text style={{
                                      fontSize: 10,
                                      color: isSelected
                                        ? colors.primary
                                        : "rgba(0, 0, 0, 0.5)",
                                      fontWeight: "400",
                                      marginTop: 2,
                                      letterSpacing: -0.1,
                                    }}>
                                      {subtitle}
                                    </Text>
                                  )}
                                </View>
                              </TouchableOpacity>
                            </Animated.View>
                          );
                        })}
                      </View>
                    </Collapsible>
                  </Animated.View>
                );
              })}
            </View>
          )}
          <View style={{ margin: 150 }} />
        </ScrollView>
      </KeyboardAwareScrollView >

      <View
        intensity={100}
        tint="light"
        style={{
          paddingBottom: safeInsets.bottom + 10,
          ...styles.bottomBarContainer,
        }}
      >
        <Centered style={{ width: "100%" }}>
          <Row>
            <Button
              type="secondary"
              style={{
                marginTop: 12,
                borderColor: "rgba(0,0,0,0.2)",
              }}
              label="Clear"
              onPress={() => {
                dispatch(
                  resetSearchFilter({
                    profession_ids: [],
                    mapDistance: 40,
                  })
                );
                setSelectedProfessions([]);
                setSelectedInternationalCountry("");
                setDatePosted({ created_min: null, created_max: null });
                props.navigation.goBack();
              }}
            />
            <Button
              type="primary"
              style={{ marginTop: 12 }}
              label="Save"
              onPress={() => {
                setFilterText("");
                if (parseInt(maxPrice) < parseInt(minPrice)) {
                  Alert.alert(
                    "Invalid Salary Range",
                    "Please enter a valid salary range"
                  );
                } else {
                  let allFilters = convertSearchFilters({
                    ...allDateTime,
                    mapDistance,
                    minPrice,
                    maxPrice,
                    location: filters.location,
                    profession_ids: selectedProfessions,
                    jobType: selectedJobType,
                    states: filters.states,
                    internationalFilter,
                    datePosted,
                  });
                  dispatch(setSearchFilter(allFilters));
                  props.navigation.goBack();
                }
              }}
            />
          </Row>
        </Centered>
      </View>
    </>
  );
}

const styles = {
  jobsAvailableTitle: {
    fontSize: 24,
    fontFamily: "NotoSans-Bold",
    letterSpacing: -1,
    marginLeft: 10,
  },
  divider: {
    borderBottomWidth: 1,
    borderColor: "rgba(0,0,0,0.1)",
    marginTop: 20,
    marginBottom: 0,
  },
  backButtonContainer: {
    backgroundColor: "rgba(0,0,0,0)",
    justifyContent: "center",
    paddingRight: 0,
    borderRadius: 14,
    paddingVertical: 10,
  },
  bottomBarContainer: {
    position: "absolute",
    backgroundColor: "white",
    bottom: 0,
    left: 0,
    right: 0,
  },
  sectionTitle: {
    fontSize: normalize(10),
    fontWeight: "bold",
    marginTop: 30,
    marginBottom: 30,
  },
  priceIcon: {
    fontWeight: "bold",
    fontSize: normalize(12),
    color: "grey",
  },
  distanceInput: {
    borderWidth: 1,
    paddingHorizontal: 24,
    fontSize: normalize(14),
    paddingVertical: 8,
    borderRadius: 10,
    borderColor: "rgba(0,0,0,0.1)",
    fontWeight: "bold",
  },
};
